import { EmailTemplateModel } from '@/typings/api/emailCenter';
import { F } from '@/utils/intl';
import { PlusOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'antd';
import React, { useCallback } from 'react';
import SectionCard from './SectionCard';
import { createEmptySection, FormSection, reorderSections } from './sections';

import styles from './AutomationsTab.less';

interface SectionsBuilderProps {
  value?: FormSection[];
  onChange?: (next: FormSection[]) => void;
  templates: EmailTemplateModel[];
}

/** Ordered, drag-sortable list of automation steps. Controlled component. */
const SectionsBuilder: React.FC<SectionsBuilderProps> = ({
  value = [],
  onChange,
  templates,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onChange?.(reorderSections(value, String(active.id), String(over.id)));
      }
    },
    [value, onChange],
  );

  return (
    <div className={styles.sectionsBuilder}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={value.map((section) => section.uid)}
          strategy={verticalListSortingStrategy}
        >
          {value.map((section, index) => (
            <SectionCard
              key={section.uid}
              section={section}
              index={index}
              templates={templates}
              onChange={(next) =>
                onChange?.(
                  value.map((entry) =>
                    entry.uid === section.uid ? next : entry,
                  ),
                )
              }
              onRemove={() =>
                onChange?.(value.filter((entry) => entry.uid !== section.uid))
              }
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        block
        onClick={() => onChange?.([...value, createEmptySection()])}
      >
        <F
          id="pages.emailCenter.automations.section.addSection"
          defaultMessage="Add step"
        />
      </Button>
    </div>
  );
};

export default SectionsBuilder;
